import { AxiosError } from 'axios';
import capitalize from 'lodash/capitalize';

import {
    NETWORK_ERROR,
    SERVER_ERROR,
    UNKNOWN_ERROR,
} from 'constants/toast-messages';

import { toastError } from 'components/CustomToast/customToastFunctions';

type ErrorData = Record<string, string | string[]>;

interface ErrorResponse {
    message?: string;
    error?: string;
    detail?: string;
    [key: string]: unknown;
}

const formatValidationErrors = (data: ErrorData): string => {
    if ('detail' in data && typeof data.detail === 'string') {
        return data.detail;
    }

    return Object.entries(data)
        .flatMap(([field, errors]) => {
            const label = capitalize(field);
            return Array.isArray(errors)
                ? errors.map((error) => `${label}: ${error}`)
                : [`${label}: ${errors}`];
        })
        .join('\n');
};

const isValidationError = (data: unknown): data is ErrorData => {
    if (typeof data !== 'object' || data === null) return false;

    return Object.values(data).some(
        (value) => typeof value === 'string' || Array.isArray(value)
    );
};

export const handleAxiosError = (
    error: unknown,
    customErrorHandler?: (message: string) => void
): string => {
    const handler = customErrorHandler ?? toastError;
    let errorMessage = UNKNOWN_ERROR;

    if (error instanceof AxiosError) {
        const { response, request, message } = error;

        if (response?.data) {
            const { data } = response;

            if (typeof data === 'string') {
                errorMessage = data;
            } else if (isValidationError(data)) {
                errorMessage = formatValidationErrors(data);
            } else if (typeof data === 'object') {
                const errorResponse = data as ErrorResponse;
                errorMessage =
                    errorResponse.message ||
                    errorResponse.error ||
                    errorResponse.detail ||
                    JSON.stringify(data);
            }
        } else if (request) {
            errorMessage = NETWORK_ERROR;
        } else {
            errorMessage = message || SERVER_ERROR;
        }
    } else if (error instanceof Error) {
        errorMessage = error.message || UNKNOWN_ERROR;
    }

    console.error('API Error:', {
        error,
        extractedMessage: errorMessage,
    });

    handler(errorMessage);
    return errorMessage;
};
